import useApi from '../../api/useApi'
import ConfigurationContext from '../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { VideoSession } from '../video/VideoSession'
import { readFile } from './file/readFile'
import { useBaseProcessor } from './useBaseProcessor'
import { useTrackSessionContext } from '../trackSession/TrackSessionContext'
import {
  TelemetryType,
  type RawTelemetryData,
  type GpsReading,
  type GyroscopeReading,
  type AccelerometerReading,
  type Device,
  calculateDistance,
} from '@pitbrain/shared'
import telementry from './telemetry/telemetry'
import type {
  GoProRoot,
  GoProGyroSample,
  GoProAcclSample,
} from './gopro/GoProTelemetryTypes'

export const useGoProProcessor = (
  setProgressPercent: React.Dispatch<React.SetStateAction<number>>
) => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { setVideoSessions } = useTrackSessionContext()
  const { loadTrack, getFirstCoordinate, processAndCreateSession } =
    useBaseProcessor()
  const { error: apiError } = api

  /**
   * Parse GoPro telemetry data into raw telemetry format
   */
  const parseGoProToRawTelemetry = async (
    results: Array<{ rawData: any; timing: any }> // eslint-disable-line
  ): Promise<RawTelemetryData> => {
    const gps5: GoProRoot = await telementry(results, {
      stream: ['GPS5'],
      repeatSticky: true,
      repeatHeaders: true,
      GPS5Fix: 2,
      GPS5Precision: 500,
    })
    const gyro: GoProRoot = await telementry(results, {
      stream: 'GYRO',
      repeatSticky: true,
      repeatHeaders: true,
    })
    const accl: GoProRoot = await telementry(results, {
      stream: 'ACCL',
      repeatSticky: true,
      repeatHeaders: true,
    })

    let totalDistance = 0
    const gpsReadings: GpsReading[] =
      gps5[1].streams.GPS5?.samples.reduce<GpsReading[]>(
        (accumulated, currentSample, index, allSamples) => {
          if (index > 0) {
            const previousSample = allSamples[index - 1]
            if (
              previousSample['GPS (Lat.) [deg]'] != null &&
              previousSample['GPS (Long.) [deg]'] != null &&
              currentSample['GPS (Lat.) [deg]'] != null &&
              currentSample['GPS (Long.) [deg]'] != null
            ) {
              const distance = calculateDistance(
                previousSample['GPS (Lat.) [deg]'],
                previousSample['GPS (Long.) [deg]'],
                currentSample['GPS (Lat.) [deg]'],
                currentSample['GPS (Long.) [deg]']
              )
              totalDistance += distance
            }
          }

          if (
            currentSample['GPS (Lat.) [deg]'] != null &&
            currentSample['GPS (Long.) [deg]'] != null
          ) {
            accumulated.push({
              latitude: currentSample['GPS (Lat.) [deg]'],
              longitude: currentSample['GPS (Long.) [deg]'],
              altitude: currentSample['GPS (Alt.) [m]'],
              speedMs: currentSample['GPS (2D speed) [m/s]'],
              timestamp: new Date(currentSample.date),
              distanceMeters: totalDistance,
            })
          }

          return accumulated
        },
        []
      ) ?? []

    const gyroReadings: GyroscopeReading[] | undefined =
      gyro[1].streams.GYRO?.samples.map((g: GoProGyroSample) => ({
        x: g['Gyroscope (x) [rad/s]'],
        y: g['Gyroscope (y) [rad/s]'],
        z: g['Gyroscope (z) [rad/s]'],
        timestamp: new Date(g.date),
        synchronizationTime: g.cts,
        unit: 'rad/s' as const,
      }))

    const acclReadings: AccelerometerReading[] | undefined =
      accl[1].streams.ACCL?.samples.map((g: GoProAcclSample) => ({
        x: g['Accelerometer (x) [m/s2]'],
        y: g['Accelerometer (y) [m/s2]'],
        z: g['Accelerometer (z) [m/s2]'],
        timestamp: new Date(g.date),
        cts: g.cts,
        unit: 'm/s²' as const,
      }))

    return {
      gps: gpsReadings,
      gyroscope: gyroReadings,
      accelerometer: acclReadings,
      recordingStartTime: gpsReadings[0]?.timestamp,
      // TODO: Get device version from file.
      device: {
        deviceName: 'GoPro',
        deviceType: 'GoPro',
        deviceVersion: '8',
      },
    }
  }

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    // GoPro (LRV) file processing
    const progress = (percent: number) => {
      setProgressPercent(percent)
    }

    const storedTrackSession = await api.trackSessionApi.getTrackSessionByName(
      file.name,
      [TelemetryType.Gps]
    )
    if (storedTrackSession) {
      const sessionFeatures = { hasVideo: true, hasLeanAngle: true }
      await loadTrack(storedTrackSession, sessionFeatures)
      setVideoSessions((prevState) => [
        ...prevState,
        new VideoSession(storedTrackSession.id, file),
      ])
      return
    }

    const cancellationToken = { cancelled: false }
    const results = []
    const result = await readFile(file, { progress, cancellationToken })
    if (result == null) return

    results.push(result)
    const rawTelemetry = await parseGoProToRawTelemetry(results)
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) return

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'GoPro', version: '8' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: true, hasVideo: true },
      file.name,
      device
    )
    setVideoSessions((prevState) => [
      ...prevState,
      new VideoSession(sessionId, file),
    ])
  }

  return { processFile, error: apiError }
}
